import { redirect } from 'react-router-dom';
import { getParamsFromStoreRedux } from 'store/utils';
import { getReturnUrlFromRequestLoader } from 'utils/common/route';
import { ensureUserInfoLoaded } from 'utils/loaderUtils';
import { PERMISSION } from 'static/Permission';

import * as globalActions from '@redux/global/actions';
import { actionCollection } from 'hooks/useFilterGrid';

const updateFilterOwnerProduct = (dispatch) => {
  dispatch(globalActions[actionCollection['product'].action](['owner']));
};

const removeLeadingSlash = (returnUrl) => {
  return returnUrl.includes('/')
    ? returnUrl.replace(/^\//, '') || 'dashboard'
    : returnUrl;
};

export const landingPageLoader =
  (queryClient) =>
  async ({ request }) => {
    const { dispatch, getState } = getParamsFromStoreRedux();

    if (!dispatch || !getState) {
      throw redirect('/login');
    }

    const returnUrl = getReturnUrlFromRequestLoader(request);

    try {
      // ✅ Use the common utility function
      const { userInfo } = await ensureUserInfoLoaded({
        queryClient,
        returnUrl,
      });

      const landingPage = userInfo?.landingPage?.toLowerCase();
      const memberLandingPage = userInfo?.memberLandingPage?.toLowerCase();

      const permissions = userInfo?.permissions || [];

      // ✅ Helper function to check URL abilities
      const checkUrlAbility = (url) => {
        if (!url) return false;

        url = url.toLowerCase();

        const urlAbility =
          url === 'members'
            ? permissions.includes(PERMISSION.VIEW_MEMBERS)
            : url === 'products'
              ? permissions.includes(PERMISSION.VIEW_PRODUCTS)
              : url === 'digital-media'
                ? permissions.includes(PERMISSION.VIEW_ASSETS)
                : url === 'folders'
                  ? permissions.includes(PERMISSION.VIEW_FOLDERS)
                  : url === 'reportings'
                    ? permissions.includes(PERMISSION.VIEW_REPORTING)
                    : false;
        return urlAbility;
      };

      // ✅ Main redirect logic
      let finalReturnUrl = returnUrl;

      // If we have a returnUrl, use it directly
      if (returnUrl && returnUrl !== '/') {
        finalReturnUrl = returnUrl;
        finalReturnUrl = removeLeadingSlash(finalReturnUrl);
        throw redirect(`/${finalReturnUrl}`);
      }
      // Post Login Screen for Suppliers
      if (
        userInfo?.member?.memberType === 'Manufacturer' ||
        userInfo?.member?.memberType === 'Shared Only'
      ) {
        const hasPermissionProductsPage = checkUrlAbility('products');
        if (hasPermissionProductsPage) {
          finalReturnUrl = 'products';
        }

        updateFilterOwnerProduct(dispatch);
        finalReturnUrl = removeLeadingSlash(finalReturnUrl);
        throw redirect(`/${finalReturnUrl}`);
      }
      // Check user's landing page preference
      else if (landingPage) {
        const checkUrl = checkUrlAbility(landingPage);

        if (checkUrl) {
          finalReturnUrl = landingPage;
        } else if (memberLandingPage) {
          const memberCheckUrl = checkUrlAbility(memberLandingPage);
          if (memberCheckUrl) {
            finalReturnUrl = memberLandingPage;
          } else {
            finalReturnUrl = finalReturnUrl || '/dashboard';
          }
        } else {
          finalReturnUrl = finalReturnUrl || '/dashboard';
        }
      }

      // Check member landing page
      else if (memberLandingPage) {
        const checkUrl = checkUrlAbility(memberLandingPage);
        if (checkUrl) {
          finalReturnUrl = memberLandingPage;
        } else {
          finalReturnUrl = finalReturnUrl || '/dashboard';
        }
      }
      // Default fallback
      else {
        if (!finalReturnUrl || finalReturnUrl === '/') {
          finalReturnUrl = '/dashboard';
        }
      }

      finalReturnUrl = removeLeadingSlash(finalReturnUrl);

      throw redirect(`/${finalReturnUrl}`);
    } catch (error) {
      // If it's already a redirect, re-throw it
      if (error?.status === 302 || error instanceof Response) {
        throw error;
      }

      // For other errors, the utility function should have handled logout
      throw redirect(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  };
